/**
 * Redirect URIs of Minga's MCP authorization server, and the verified clients among them (decision 0046, amended
 * 2026-10-12 and 2026-10-21).
 *
 * Since 2026-10-21 a client may register any `https:` callback (`isHttpsRedirect`) next to loopback `http:` for CLI
 * clients, so a new MCP client can connect without a code change. The safeguard is the consent page: it names a client
 * by the host its callback is on, never by the name it registered. The exact callbacks below are the **verified**
 * clients: the consent page shows them by vendor name and host ("Claude (claude.ai)"); any other `https:` callback is
 * shown as unverified, with its real host in bold, a warning, and the registered name only as "calls itself …".
 * Verified callbacks also keep their own registration rate-limit bucket (the vendors register from shared egress IPs).
 *
 * Sources (fetched 2026-10-12, see docs/investigations/2026-10-12-mcp-web-connectors.md):
 * - Claude (claude.ai, claude.com, Desktop's Connectors, mobile, Cowork): https://claude.com/docs/connectors/building/authentication
 *   — the callback is `https://claude.ai/api/mcp/auth_callback`; Anthropic asks servers that allowlist callbacks to add
 *   `https://claude.com/api/mcp/auth_callback` too, because the host may move.
 * - Every Agent (agent.every.to, added 2026-10-21): `https://agent.every.to/api/dashboard/connections/custom_mcp/oauth/callback`,
 *   the callback Every Agent's custom-MCP connection sends; taken from Every's own failed registration (our 400
 *   `invalid_redirect_uri`) as reported by an Every engineer, for Every Agent ↔ Minga messaging (decision 0140 #25).
 * - ChatGPT: https://developers.openai.com/apps-sdk/build/auth — `https://chatgpt.com/connector_platform_oauth_redirect`
 *   when the authorization server meets RFC 9207 (ours does: `iss` on every authorization response). The legacy
 *   per-connector form `https://chatgpt.com/connector/oauth/{callback_id}` is not listed (it is allowed, unverified).
 *
 * Compared as exact strings: no URL normalisation, so `https://CLAUDE.ai/…`, `https://claude.ai:443/…`, a trailing
 * slash or a query are not verified (they are shown as unverified, by their host; a fragment is refused). No imports: `apps/cloud/src/auth/options.ts` loads this file under Node
 * (the auth migration generator).
 */

export interface HostedConnector {
  /** The product name the consent page shows. */
  readonly name: string
  /** The verified host the code goes to (the callback's host), shown next to the name. */
  readonly host: string
  /** The short attribution name (`via: mcp:<client>`). */
  readonly client: string
}

const CLAUDE_AI: HostedConnector = { name: "Claude", host: "claude.ai", client: "claude" }
const CLAUDE_COM: HostedConnector = { name: "Claude", host: "claude.com", client: "claude" }
const CHATGPT: HostedConnector = { name: "ChatGPT", host: "chatgpt.com", client: "chatgpt" }
const EVERY_AGENT: HostedConnector = { name: "Every Agent", host: "agent.every.to", client: "every" }

/** Exact callback URL → the connector it belongs to. Add a line only from the vendor's own documentation. */
export const HOSTED_CONNECTOR_CALLBACKS: ReadonlyMap<string, HostedConnector> = new Map([
  ["https://claude.ai/api/mcp/auth_callback", CLAUDE_AI],
  ["https://claude.com/api/mcp/auth_callback", CLAUDE_COM],
  ["https://chatgpt.com/connector_platform_oauth_redirect", CHATGPT],
  ["https://agent.every.to/api/dashboard/connections/custom_mcp/oauth/callback", EVERY_AGENT],
])

/** The hosted connector whose callback is exactly `uri`, or null. */
export const hostedConnectorOf = (uri: string | null | undefined): HostedConnector | null =>
  typeof uri === "string" ? (HOSTED_CONNECTOR_CALLBACKS.get(uri) ?? null) : null

export const isHostedConnectorCallback = (uri: string): boolean => hostedConnectorOf(uri) !== null

// ---- Which redirect URIs a client may use -----------------------------------------------------------------------------

/** The WHATWG `URL` global (Workers, browsers, Node): this package compiles without DOM or Node types. */
declare const URL: new (
  input: string,
) => {
  readonly protocol: string
  readonly hostname: string
  readonly host: string
  readonly username: string
  readonly password: string
}
type ParsedUrl = InstanceType<typeof URL>

const parse = (uri: string): ParsedUrl | null => {
  try {
    return new URL(uri)
  } catch {
    return null
  }
}

/** `http:` on localhost, 127.0.0.1 or [::1], without userinfo or fragment: the redirects of CLI clients. */
export const isLoopbackHttpRedirect = (uri: string): boolean => {
  if (uri.includes("#")) return false
  const url = parse(uri)
  if (!url || url.protocol !== "http:" || url.username !== "" || url.password !== "") return false
  return url.hostname === "localhost" || url.hostname === "127.0.0.1" || url.hostname === "[::1]"
}

/**
 * An IPv4 or IPv6 literal, as `URL` normalises it (`0x7f.1` → `127.0.0.1`, `[::FFFF:10.0.0.1]` → `[::ffff:a00:1]`), in
 * a private, loopback, link-local, shared, benchmark, multicast or reserved range.
 */
export const isPrivateIpHost = (hostname: string): boolean => {
  const v4 = /^(\d{1,3})\.(\d{1,3})\.(\d{1,3})\.(\d{1,3})$/.exec(hostname)
  if (v4) {
    const a = Number(v4[1])
    const b = Number(v4[2])
    return (
      a === 0 ||
      a === 10 ||
      a === 127 ||
      (a === 100 && b >= 64 && b <= 127) ||
      (a === 169 && b === 254) ||
      (a === 172 && b >= 16 && b <= 31) ||
      (a === 192 && b === 168) ||
      (a === 192 && b === 0 && Number(v4[3]) === 0) ||
      (a === 198 && (b === 18 || b === 19)) ||
      a >= 224
    )
  }
  if (hostname.startsWith("[") && hostname.endsWith("]")) {
    const v6 = hostname.slice(1, -1).toLowerCase()
    // ::, ::1, IPv4-mapped or -compatible (::ffff:…), unique local fc00::/7, link-local fe80::/10, multicast ff00::/8
    return v6.startsWith("::") || /^(f[cd]|fe[89ab]|ff)/.test(v6)
  }
  return false
}

/**
 * An `https:` redirect a client may use (decision 0046, amended 2026-10-21): an absolute URL with a host, without
 * userinfo or fragment, not on localhost and not an IP literal in a private range. Codes then go to that host, which
 * the consent page names.
 */
export const isHttpsRedirect = (uri: string): boolean => {
  // the authority as written: `URL` would read `https:///cb` or `https:\\host` as a host of its own choosing
  if (uri.includes("#") || !/^https:\/\/[^/\\?#@]/i.test(uri)) return false
  const url = parse(uri)
  if (!url || url.protocol !== "https:" || url.hostname === "") return false
  if (url.username !== "" || url.password !== "") return false
  const host = url.hostname.toLowerCase()
  if (host === "localhost" || host.endsWith(".localhost")) return false
  return !isPrivateIpHost(host)
}

/** The redirects a client may use, at registration and at authorize: loopback `http:` or an allowed `https:` URI. */
export const isAllowedRedirect = (uri: string): boolean => isLoopbackHttpRedirect(uri) || isHttpsRedirect(uri)

/**
 * How far Minga vouches for where a code goes: `local` (loopback, the person's own machine), `verified` (exactly a
 * listed vendor callback) or `unverified` (any other host, and anything unreadable).
 */
export type RedirectTrust = "local" | "verified" | "unverified"

export const redirectTrustOf = (uri: string | null | undefined): RedirectTrust => {
  if (typeof uri !== "string") return "unverified"
  if (hostedConnectorOf(uri)) return "verified"
  return isLoopbackHttpRedirect(uri) ? "local" : "unverified"
}

/** The host (with its port, if any) a redirect URI sends the code to, or null when it isn't a URL with a host. */
export const redirectHostOf = (uri: string | null | undefined): string | null =>
  typeof uri === "string" ? parse(uri)?.host || null : null
